/**
 * IdeaComments Component
 * 
 * Comments section for an idea with threaded replies.
 * Includes comment input and list display.
 */

import React, { useState, useCallback } from 'react';
import { View, Text, TextInput, Pressable, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, icon } from '../../../shared/tokens/mobile';
import { IdeaComment } from '../../../shared/types/idea';
import { styles } from './styles/ideaCommentsStyles';
import { formatUserContent } from '../../../shared/utils/textHelpers';

interface IdeaCommentsProps {
  comments: IdeaComment[];
  loading: boolean;
  onAddComment: (content: string, parentId?: number) => Promise<boolean>;
  commenting: boolean;
}

export function IdeaComments({ comments, loading, onAddComment, commenting }: IdeaCommentsProps) {
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!newComment.trim() || commenting) return;
    
    const success = await onAddComment(newComment.trim(), replyingTo || undefined);
    if (success) {
      setNewComment('');
      setReplyingTo(null);
    }
  }, [newComment, replyingTo, onAddComment, commenting]);

  const handleReply = useCallback((commentId: number) => {
    setReplyingTo(replyingTo === commentId ? null : commentId);
  }, [replyingTo]);

  // Build threaded structure
  const topLevelComments = comments.filter(c => !c.parent_id);
  const getReplies = (parentId: number) => comments.filter(c => c.parent_id === parentId);

  if (loading && comments.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={color.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Comment Input */}
      <View style={styles.inputContainer}>
        {replyingTo && (
          <View style={styles.replyingBanner}>
            <Text style={styles.replyingText}>Replying to comment</Text>
            <Pressable onPress={() => setReplyingTo(null)}>
              <Ionicons name="close" size={icon.sm} color={color.textMuted} />
            </Pressable>
          </View>
        )}
        <View style={styles.inputRow}>
          <TextInput
            style={styles.input}
            placeholder="Add a comment..."
            placeholderTextColor={color.textMuted}
            value={newComment}
            onChangeText={setNewComment}
            multiline
            maxLength={1000}
          />
          <Pressable
            onPress={handleSubmit}
            disabled={!newComment.trim() || commenting}
            style={[styles.submitButton, (!newComment.trim() || commenting) && styles.submitButtonDisabled]}
          >
            {commenting ? (
              <ActivityIndicator size="small" color={color.surface} />
            ) : (
              <Ionicons name="send" size={icon.sm} color={color.surface} />
            )}
          </Pressable>
        </View>
      </View>

      {/* Comments List */}
      {comments.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="chatbubble-outline" size={icon.xl} color={color.textMuted} />
          <Text style={styles.emptyText}>No comments yet</Text>
          <Text style={styles.emptySubtext}>Start the discussion</Text>
        </View>
      ) : (
        <View style={styles.commentsList}>
          {topLevelComments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              replies={getReplies(comment.id)}
              onReply={handleReply}
              isReplyTarget={replyingTo === comment.id}
              depth={0}
            />
          ))}
        </View>
      )}
    </View>
  );
}

// CommentItem sub-component
interface CommentItemProps {
  comment: IdeaComment;
  replies: IdeaComment[];
  onReply: (commentId: number) => void;
  isReplyTarget: boolean;
  depth: number;
}

function CommentItem({ comment, replies, onReply, isReplyTarget, depth }: CommentItemProps) {
  const authorName = comment.author?.display_name || comment.author?.email.split('@')[0] || 'Unknown';
  const timeAgo = getTimeAgo(new Date(comment.created_at));

  return (
    <View style={[styles.commentContainer, depth > 0 && styles.replyContainer]}>
      <View style={styles.commentHeader}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{authorName.charAt(0).toUpperCase()}</Text>
        </View>
        <View style={styles.commentMeta}>
          <Text style={styles.authorName}>{authorName}</Text>
          <Text style={styles.timeAgo}>{timeAgo}</Text>
        </View>
      </View>
      
      <Text style={styles.commentContent}>{formatUserContent(comment.content)}</Text>
      
      <View style={styles.commentActions}>
        <Pressable
          onPress={() => onReply(comment.id)}
          style={[styles.replyButton, isReplyTarget && styles.replyButtonActive]}
        >
          <Ionicons name="return-down-forward-outline" size={14} color={isReplyTarget ? color.primary : color.textMuted} />
          <Text style={[styles.replyButtonText, isReplyTarget && styles.replyButtonTextActive]}>Reply</Text>
        </Pressable>
      </View>

      {depth < 2 && replies.length > 0 && (
        <View style={styles.repliesContainer}>
          {replies.map(reply => (
            <CommentItem key={reply.id} comment={reply} replies={[]} onReply={onReply} isReplyTarget={false} depth={depth + 1} />
          ))}
        </View>
      )}
    </View>
  );
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
